import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Opportunity } from '../../types/opportunity';
import { RootState } from '../store';
import { fetchItems } from '../queries';
import { Filter, TemporaryUserView, UserView } from '../../types/user';
import type { DatePresetId } from '../opportunityDatePresets';
import { defaultColumns, defaultViews } from '../config/tables';

type SalesforceOpportunitiesState = {
  views: {
    [viewId: string]: {
      opportunities: Opportunity[];
      totalCount: number;
      page: number;
      size: number;
      view: UserView | TemporaryUserView;
      search: string;
      // A pre-built close-date range, already resolved to filters. Page state only: not saved with the view.
      datePreset: { id: DatePresetId; filters: Filter[] } | null;
    };
  };
  initialLoadComplete: boolean;
  loading: boolean;
  error: string | null;
  taggedOpportunityIds: string[];
  taggedOpportunitiesLoaded: boolean;
  taggedOpportunitiesError: string | null;
  needsRefresh: boolean; // something outside of the table/list view made a change to opp data
};

const initialState: SalesforceOpportunitiesState = {
  views: {},
  initialLoadComplete: false,
  loading: false,
  error: null,
  taggedOpportunityIds: [],
  taggedOpportunitiesLoaded: false,
  taggedOpportunitiesError: null,
  needsRefresh: false,
};

export const fetchSalesforceOpportunities = createAsyncThunk<
  { opportunities: Opportunity[]; totalCount: number; view: UserView | TemporaryUserView },
  { view: UserView | TemporaryUserView },
  { state: RootState }
>(
  'salesforceOpportunities/fetchSalesforceOpportunities',
  async ({ view }, { getState }) => {
    const state: RootState = getState();
    const isCustomerView = view.id.toString().startsWith('customer-view-');
    const { page = 0, size = isCustomerView ? 5 : 30 } = state.salesforceOpportunities.views[String(view.id)] || {};
    const filters = view.filters ? [...view.filters] : [];
    if (view.userOwnedOnly) {
      const userOpportunities = state.salesforceOpportunities.taggedOpportunityIds;
      filters.push({
        comparison: userOpportunities,
        operator: "anyOf",
        prop: "Opportunity_ID_18__c"
      });
    }
    const datePresetFilters = state.salesforceOpportunities.views[String(view.id)]?.datePreset?.filters;
    if (datePresetFilters) {
      filters.push(...datePresetFilters);
    }
    const body = {
      filters,
      order_by: `${view.orderBy?.column ?? 'Name'} ${view.orderBy?.order?.toUpperCase() ?? 'ASC'}`,
      columns: [...(view.columns ?? defaultColumns.opportunity).map((column) => column.id), 'se_phase'],
      search_string: state.salesforceOpportunities.views[String(view.id)]?.search ?? ''
    };
    const fetchedData = await fetchItems('sf/opportunity', { page: page + 1, size }, { pagination: true, body });
    // Tolerate both response shapes: the paginated envelope `{ items, total }` this and the
    // account/customer endpoints are built for, and a bare array of records. The dev proxy has been
    // seen returning a bare array for `sf/opportunity`, which would otherwise leave `.items`
    // undefined and render an empty table. Fall back to the row count for the total in that case.
    const rows = Array.isArray(fetchedData) ? fetchedData : (fetchedData.items ?? []);
    const opportunities = rows.map((opportunity) => ({
      ...opportunity,
      id: opportunity?.Id ?? opportunity?.id, // Need to make sure 'id' exists since somewhere hbr-dom-table overwrites slot and key ids, and expects 'id' to be present
    } as Opportunity));
    const totalCount = Array.isArray(fetchedData) ? rows.length : (fetchedData.total ?? rows.length);
    return { opportunities, totalCount, view };
  }
);

export const fetchTaggedOpportunities = createAsyncThunk<
  string[],
  void,
  { state: RootState }
>(
  'salesforceOpportunities/fetchTaggedOpportunities',
  async (_arg,{ getState }) => {
    const state = getState();
    const tagged = await fetchItems('sf/query', {
      q: `SELECT Id, OpportunityId, UserId from OpportunityTeamMember WHERE UserId = '${state.user.userData.sfId}'`
    });
    return (tagged ?? []).map(item => item.OpportunityId);
  }
);

export const checkTaggedOpportunitiesLoaded = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (!state.salesforceOpportunities.taggedOpportunitiesLoaded) {
    await dispatch(fetchTaggedOpportunities());
  }
};

// --- The Slice ---
const opportunitiesSlice = createSlice({
  name: 'salesforceOpportunities',
  initialState,
  reducers: {
    removeOpportunityView(state, action: PayloadAction<{ viewId: string | number }>) {
      if (state.views[String(action.payload.viewId)]) {
        delete state.views[String(action.payload.viewId)];
      }
    },
    setOpportunityView(state, action: PayloadAction<{ viewId: string | number, options?: Record<string, any> }>) {
      state.views[String(action.payload.viewId)] = {
        opportunities: [],
        totalCount: 0,
        page: 0,
        size: 30,
        view: action.payload.options?.view ?? defaultViews.opportunity,
        search: '',
        datePreset: null,
      };
    },
    setPage(state, action: PayloadAction<{ viewId: string | number, page: number }>) {
      const { viewId, page } = action.payload;
      if (state.views[String(viewId)]) {
        state.views[String(viewId)].page = page;
      }
    },
    setSize(state, action: PayloadAction<{ viewId: string | number, size: number }>) {
      const { viewId, size } = action.payload;
      if (state.views[String(viewId)]) {
        state.views[String(viewId)].size = size;
        state.views[String(viewId)].page = 0;
      }
    },
    setSearch(state, action: PayloadAction<{ viewId: string | number, search: string }>) {
      const { viewId, search } = action.payload;
      if (state.views[String(viewId)]) {
        state.views[String(viewId)].search = search;
      }
    },
    setDatePreset(
      state,
      action: PayloadAction<{ viewId: string | number, datePreset: { id: DatePresetId; filters: Filter[] } | null }>
    ) {
      const { viewId, datePreset } = action.payload;
      if (state.views[String(viewId)]) {
        state.views[String(viewId)].datePreset = datePreset;
      }
    },
    tagOpportunity(state, action: PayloadAction<string>) {
      state.taggedOpportunityIds = [...state.taggedOpportunityIds, action.payload];
    },
    untagOpportunity(state, action: PayloadAction<string>) {
      state.taggedOpportunityIds = state.taggedOpportunityIds.filter(id => id !== action.payload);
    },
    setNeedsRefresh(state, action: PayloadAction<boolean>) {
      state.needsRefresh = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesforceOpportunities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesforceOpportunities.fulfilled, (
        state,
        action: PayloadAction<{ opportunities: Opportunity[]; totalCount: number; view: UserView | TemporaryUserView }>
      ) => {
        const { opportunities, totalCount, view } = action.payload;
        state.views[String(view.id)] = {
          ...state.views[String(view.id)],
          opportunities,
          totalCount,
          view
        };
        state.initialLoadComplete = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSalesforceOpportunities.rejected, (state, action) => {
        state.initialLoadComplete = true;
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchTaggedOpportunities.pending, (state) => {
        state.taggedOpportunitiesLoaded = false;
        state.taggedOpportunitiesError = null;
      })
      .addCase(fetchTaggedOpportunities.fulfilled, (state, action) => {
        state.taggedOpportunityIds = action.payload;
        state.taggedOpportunitiesLoaded = true;
        state.taggedOpportunitiesError = null;
      })
      .addCase(fetchTaggedOpportunities.rejected, (state, action) => {
        state.taggedOpportunitiesLoaded = false;
        state.taggedOpportunitiesError = action.error.message;
      })
  }
});

export const {
  removeOpportunityView,
  setOpportunityView,
  setPage,
  setSize,
  setSearch,
  setDatePreset,
  tagOpportunity,
  untagOpportunity,
  setNeedsRefresh,
} = opportunitiesSlice.actions;

export default opportunitiesSlice.reducer;